import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Send, CheckCircle, AlertCircle, MapPin, Phone, Mail, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

export const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState(null); // 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handlePrefill = (e) => {
      const plan = e.detail?.plan;
      if (!plan) return;
      const msg = language === 'fr'
        ? `Bonjour, je souhaite recevoir une offre pour le plan ${plan}.`
        : `Hello, I would like to receive a quote for the ${plan} plan.`;
      setFormData(prev => ({ ...prev, message: msg }));
    };
    window.addEventListener('prefill-contact', handlePrefill);
    return () => window.removeEventListener('prefill-contact', handlePrefill);
  }, [language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      await axios.post('/api/contact', {
        ...formData,
        language,
      });
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      
      // Reset status after 5 seconds
      setTimeout(() => setStatus(null), 5000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || t('contact_error'));
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          {...fadeUp}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight" data-testid="contact-title">
            {t('contact_title')}
          </h2>
          <p className="text-base md:text-lg text-slate-600">
            {t('contact_subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Contact Info Card */}
          <motion.div
            {...fadeUp}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm h-fit"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">LOGITRAK SA</h3>
            </div>

            <div className="space-y-4">
              {/* Address */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    {language === 'fr' ? 'Adresse' : 'Address'}
                  </p>
                  <p className="text-slate-600">Chem. de la Rueyre 116</p>
                  <p className="text-slate-600">1020 Renens, Suisse</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Phone className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    {language === 'fr' ? 'Téléphone' : 'Phone'}
                  </p>
                  <a 
                    href="tel:+41216354422" 
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    data-testid="contact-phone"
                  >
                    +41 21 635 44 22
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Email</p>
                  <a 
                    href="mailto:info@logitrak.ch" 
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    data-testid="contact-email-link"
                  >
                    info@logitrak.ch
                  </a>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="mt-6 rounded-lg overflow-hidden border border-slate-200">
              <iframe
                title="LOGITRAK SA Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2745.8!2d6.583!3d46.533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c31c!2sChem.%20de%20la%20Rueyre%20116%2C%201020%20Renens!5e0!3m2!1sfr!2sch!4v1"
                width="100%"
                height="180"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale hover:grayscale-0 transition-all duration-300"
              ></iframe>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form 
            {...fadeUp}
            transition={{ delay: 0.1, duration: 0.5 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm"
            data-testid="contact-form"
          >
            <div className="space-y-4">
              {/* Name field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('contact_name')}
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  placeholder="Jean Dupont"
                  data-testid="contact-name-input"
                />
              </div>

              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('contact_email')}
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  placeholder="jean@example.com"
                  data-testid="contact-email-input"
                />
              </div>

              {/* Message field */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('contact_message')}
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg resize-none"
                  placeholder={language === 'fr' ? "Votre message ici..." : "Your message here..."}
                  data-testid="contact-message-input"
                />
              </div>

              {/* Status messages */}
              {status === 'success' && (
                <div className="flex items-center gap-2 p-4 bg-green-50 text-green-700 rounded-lg" data-testid="contact-success">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t('contact_success')}</span>
                </div>
              )}

              {status === 'error' && (
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg" data-testid="contact-error">
                  <AlertCircle className="w-5 h-5" />
                  <span>{errorMessage || t('contact_error')}</span>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                size="lg"
                disabled={status === 'loading'}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-6 font-medium transition-all"
                data-testid="contact-submit"
              >
                {status === 'loading' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('contact_sending')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('contact_send')}
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
